/**
 * DSP (Data Stream Protocol) Parser
 *
 * Pure functions for parsing the Data Stream Protocol wire format into structured state.
 * This module has zero React dependencies — it can be used in any Node/TS context.
 *
 * Wire format: `${prefix}:${JSON.stringify(value)}\n`
 *
 * Prefix codes from assistant-stream@0.3.6 DataStreamStreamChunkType:
 *   0 = TextDelta
 *   3 = Error
 *   9 = ToolCall
 *   a = ToolCallResult
 *   d = FinishMessage (no-op)
 *   e = FinishStep / DataAnnotation (used for {messageId})
 *   g = ReasoningDelta
 */

export interface MessagePart {
  type: string;
  [key: string]: unknown;
}

export interface DspParserState {
  parts: MessagePart[];
  messageId: string | null;
  error: string | null;
}

/**
 * Returns the initial empty DSP parser state.
 */
export function initialDspState(): DspParserState {
  return { parts: [], messageId: null, error: null };
}

/**
 * Pure reducer function that parses a single DSP wire-format line and returns updated state.
 *
 * Returns a new state object whenever parts/messageId/error change so consumers
 * (e.g. React) can detect updates by reference. Returns the same state instance
 * unchanged for unknown/malformed/empty lines.
 *
 * @param state - Current parser state
 * @param line  - One DSP line (without trailing newline)
 * @returns Updated state
 */
export function parseDspLine(state: DspParserState, line: string): DspParserState {
  const colonIdx = line.indexOf(':');
  if (colonIdx === -1) return state;

  const prefix = line.slice(0, colonIdx);
  let value: unknown;
  try {
    value = JSON.parse(line.slice(colonIdx + 1));
  } catch {
    return state; // skip malformed JSON
  }

  switch (prefix) {
    case '0': {
      // TextDelta — merge only when the last emitted part is also text, otherwise append.
      // Preserves temporal ordering when other part types (e.g. tool-call) appear between deltas.
      const last = state.parts.at(-1);
      if (last?.type === 'text') {
        const existing = last as { type: string; text: string };
        const updated = { ...existing, text: existing.text + (value as string) };
        const newParts = [...state.parts.slice(0, -1), updated];
        return { ...state, parts: newParts };
      }
      return {
        ...state,
        parts: [...state.parts, { type: 'text', text: value as string }],
      };
    }
    case 'g': {
      // ReasoningDelta — merge only when the last emitted part is also reasoning, otherwise append.
      // assistant-ui expects { type: 'reasoning', text: string }
      const last = state.parts.at(-1);
      if (last?.type === 'reasoning') {
        const existing = last as { type: string; text: string };
        const updated = { ...existing, text: existing.text + (value as string) };
        const newParts = [...state.parts.slice(0, -1), updated];
        return { ...state, parts: newParts };
      }
      return {
        ...state,
        parts: [...state.parts, { type: 'reasoning', text: value as string }],
      };
    }
    case '9': {
      // ToolCall
      const tc = value as { toolCallId: string; toolName: string; args: Record<string, unknown> };
      return {
        ...state,
        parts: [
          ...state.parts,
          {
            type: 'tool-call',
            toolCallId: tc.toolCallId,
            toolName: tc.toolName,
            args: tc.args,
            argsText: JSON.stringify(tc.args),
          },
        ],
      };
    }
    case 'a': {
      // ToolCallResult — merge into existing tool-call part by toolCallId, immutably
      const tr = value as { toolCallId: string; toolName?: string; result: unknown };
      const idx = state.parts.findIndex(
        (p) => p.type === 'tool-call' && p.toolCallId === tr.toolCallId,
      );
      if (idx !== -1) {
        const existing = state.parts[idx];
        const updated = { ...existing, result: tr.result, isError: false };
        const newParts = [...state.parts.slice(0, idx), updated, ...state.parts.slice(idx + 1)];
        return { ...state, parts: newParts };
      }
      return {
        ...state,
        parts: [
          ...state.parts,
          {
            type: 'tool-call',
            toolCallId: tr.toolCallId,
            toolName: tr.toolName ?? '',
            args: {},
            argsText: '',
            result: tr.result,
            isError: false,
          },
        ],
      };
    }
    case '3': {
      // Error — return new state object so change is detectable by reference
      return { ...state, error: value as string };
    }
    case 'e': {
      // DataAnnotation — extract messageId
      const annotation = value as { messageId: string };
      return { ...state, messageId: annotation.messageId };
    }
    case 'd': {
      // FinishMessage — no-op
      return state;
    }
    default:
      return state;
  }
}
